import { BASE_URL } from 'config';
import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Select,
  MenuItem,
  Button,
  Typography,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
} from "@mui/material";
import { UploadFile } from "@mui/icons-material";
import { PrimaryColor } from "assets/theme/base/themeColors";
import { validateRequired } from "validations";
import axios from "axios";
import Swal from "sweetalert2";
import { useMaterialUIController } from "context";
import { useTheme } from "@mui/material/styles";

const AddShippingMethodForm = ({ onClose, onSuccess, initialData }) => {
  const [controller] = useMaterialUIController();
  const darkMode = controller.darkMode;
  const theme = useTheme();
  const [formData, setFormData] = useState({
    name: initialData?.name || "",
    image: initialData?.image || null,
    is_active: initialData?.is_active || 1,
    id: initialData?.id || null,
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name,
        image: initialData.image,
        is_active: initialData.is_active,
        id: initialData.id,
      });
    }
  }, [initialData]);

  const [errors, setErrors] = useState({});
  const [formSubmitted, setFormSubmitted] = useState(false);

  useEffect(() => {
    if (formSubmitted) validateForm();
  }, [formData]);

  const validateForm = () => {
    const newErrors = {};
    if (!validateRequired(formData.name)) newErrors.name = "يجب إدخال الاسم";
    if (!formData.image) newErrors.image = "يجب ادخال صورة طريقة الشحن";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file && ["image/png", "image/jpeg", "image/gif"].includes(file.type)) {
      setFormData((prev) => ({ ...prev, image: file }));
      setErrors((prev) => ({ ...prev, image: "" }));
    } else {
      setErrors((prev) => ({ ...prev, image: "يجب أن تكون الصورة بصيغة PNG, jpeg, أو GIF" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitted(true);
    if (!validateForm()) return;
    try {
      const formPayload = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== null && value !== undefined && key !== "id") {
          if (key === "image") {
            if (value instanceof File) {
              formPayload.append(key, value);
            }
          } else {
            formPayload.append(key, value);
          }
        }
      });
      Swal.fire({
        title: "جاري الحفظ...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
          const container = document.querySelector(".swal2-container");
          container.style.zIndex = "99999";
        },
      });
      const token = localStorage.getItem("user-token");
      const endpoint = formData.id
        ? `${BASE_URL}/administrator/shipping-methods/update/${formData.id}`
        : BASE_URL + "/administrator/shipping-methods/store";
      const response = await axios.post(endpoint, formPayload, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });
      Swal.close();
      if (response.data.status) {
        await Swal.fire({
          icon: "success",
          title: "تم الحفظ!",
          text: "تم حفظ البيانات بنجاح",
          confirmButtonText: "موافق",
          didOpen: () => {
            const container = document.querySelector(".swal2-container");
            container.style.zIndex = "99999";
          },
        });
        onSuccess();
        onClose();
      } else {
        throw new Error(response.data.message || "حدث خطأ غير متوقع");
      }
    } catch (error) {
      Swal.close();
      const errorMessage = error.response?.data?.message || error.message || "حدث خطأ أثناء الحفظ";
      Swal.fire({
        icon: "error",
        title: "فشل الحفظ!",
        text: errorMessage,
        confirmButtonText: "موافق",
        didOpen: () => {
          const container = document.querySelector(".swal2-container");
          container.style.zIndex = "99999";
        },
      });
    } finally {
      setFormSubmitted(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: "",
      image: null,
      is_active: 1,
    });
    setErrors({});
    onClose();
  };

  return (
    <><Box
      sx={{
        p: 3,
        width: 600,
        bgcolor: darkMode ? theme.palette.background.default : "background.paper",
        borderRadius: 2,
        boxShadow: 3,
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight="bold" >
          {formData.id ? "تعديل بيانات طريقة الشحن" : "إضافة طريقة شحن جديدة"}
        </Typography>
      </Box>

      <Box display="flex" justifyContent="center" alignItems="center" gap={2}>
        <FormControl fullWidth error={!!errors.name} sx={{ mb: 2 }}>
          <TextField
            label="اسم طريقة الشحن*"
            name="name"
            value={formData.name}
            onChange={handleChange}
            error={!!errors.name}
            helperText={errors.name}
          />
        </FormControl>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="activing">حالة طريقة الشحن </InputLabel>
          <Select
            labelId="activing"
            name="is_active"
            value={formData.is_active}
            onChange={handleChange}
            sx={{ height: 43 }}
          >
            <MenuItem value="1">نشط</MenuItem>
            <MenuItem value="0">غير نشط</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <Box display="flex" gap={2}>
        <Box display="flex" flexDirection="column" gap={1}>
          <input
            type="file"
            id="upload-image"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleImageChange}
            name="image"
          />
          <label htmlFor="upload-image">
            <Button
              variant="outlined"
              component="span"
              startIcon={<UploadFile color="primary.main" />}
              sx={{ px: 8, minWidth: 180, color: "text.main", borderColor: "text.border" }}
            >
              تحميل
            </Button>
          </label>
          <Typography variant="caption" color="secondary" display="block">
            صورة او شعار طريقة الشحن
          </Typography>
          <FormHelperText error>{errors.image}</FormHelperText>
        </Box>
      </Box>
      {formData.image && (
        <Box display="flex" justifyContent="center" alignItems="center" mt={2}>
          <img
            src={
              typeof formData.image === "string"
                ? formData.image
                : URL.createObjectURL(formData.image)
            }
            alt="صورة طريقة الشحن"
            style={{
              width: 120,
              height: 100,
              objectFit: "cover",
              borderRadius: 8,
              border: "1px solid #eee",
            }}
          />
        </Box>
      )}
      <Box mt={4} display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" color="text.border">
          * الحقول المطلوبة
        </Typography>
        <Box display="flex" gap={2}>
          <Button variant="outlined" color="error" onClick={handleCancel} sx={{ px: 4, color: "text.border", borderColor: "text.border" }}>
            إلغاء
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            sx={{ px: 4, color: "white !important", backgroundColor: "primary.dark", "&:hover": { backgroundColor: "primary.main" } }}
            onClick={handleSubmit}
          >
            حفظ
          </Button>
        </Box>
      </Box>
    </Box>
    </>
  );
};

export default AddShippingMethodForm;
